import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton';
import { smallInfoLabel, Row } from 'components/GridItemList';
import { RefreshCwIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import PartyName from 'components/party/PartyName';
import { EDIT_BUTTON_CLASS } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import DebtCollectionCard, {
    receivabilityHistorySheet,
    useDebtCollectionAuditInfo,
    useSaveDebtNotes,
} from 'pages/invoices/layout/views/general-tab/DebtCollectionCard';
import InvoiceDetailsCard, {
    useRecalculateInvoiceDates,
    useServiceDateAutofill,
} from 'pages/invoices/layout/views/general-tab/InvoiceDetailsCard';
import RecipientsCard from 'pages/invoices/layout/views/general-tab/RecipientsCard';
import { ReactNode, useState } from 'react';
import { Path, useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import { InvoiceForm, InvoiceStatusType } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { InvoiceTabProps } from '../invoiceTabProps';
import { InvoiceFormSection, NO_SAVE, yesNo } from './GeneralCard';

const dateCell = (value: string | null | undefined) => <FormattedDate value={value} />;

const RECIPIENT_FIELDS: Path<InvoiceForm>[] = [
    'primaryRecipientParty',
    'primaryRecipientAdditionalEmails',
    'secondaryRecipientParties',
    'secondaryRecipientAdditionalEmails',
];

/** Recipients of invoice, saved by the limited update it takes today. */
export function RecipientsSection({
    invoice,
    isInViewMode,
    updateInvoiceLimited,
}: Pick<InvoiceTabProps, 'invoice' | 'isInViewMode' | 'updateInvoiceLimited'>) {
    const { control } = useFormContext<InvoiceForm>();
    const [primary, primaryEmails, secondary, secondaryEmails] = useWatch({
        control,
        name: [
            'primaryRecipientParty',
            'primaryRecipientAdditionalEmails',
            'secondaryRecipientParties',
            'secondaryRecipientAdditionalEmails',
        ],
    });
    const hasUpdatePermission = useHasPermissions(['UPDATE.INVOICES.ALL']);
    const { isPostponed } = useInvoice(invoice);
    // A recipient is its chip and its address after it, wrapping under it in a narrow cell; each keeps one cell.
    const recipient = (party: NonNullable<typeof primary>) => (
        <span key={party.id} className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <PartyName party={party} isLink />
            {party.mainEmail ? <span className="min-w-0 break-all">{party.mainEmail}</span> : null}
        </span>
    );
    const rows: Row[] = [
        {
            label: smallInfoLabel(
                'invoices.general-tab.recipients.primary-recipient',
                'invoices.tooltips.primary-recipient'
            ),
            content: primary ? recipient(primary) : '-',
        },
        {
            label: smallInfoLabel(
                'invoices.general-tab.recipients.specific-recipient-address',
                'invoices.tooltips.specific-recipient-address'
            ),
            content: primaryEmails || '-',
        },
        {
            label: smallInfoLabel(
                'invoices.general-tab.recipients.secondary-recipient',
                'invoices.tooltips.secondary-recipient'
            ),
            content: secondary?.length ? (
                <span className="flex min-w-0 flex-col gap-1">{secondary.map(recipient)}</span>
            ) : (
                '-'
            ),
        },
        {
            label: smallInfoLabel(
                'invoices.general-tab.recipients.other-secondary-recipients',
                'invoices.tooltips.other-secondary-recipients'
            ),
            content: secondaryEmails || '-',
        },
    ];

    return (
        <InvoiceFormSection
            id="recipients"
            titleId="invoices.general-tab.recipients.title"
            invoice={invoice}
            canEdit={!((isInViewMode && !hasUpdatePermission) || isPostponed)}
            rows={rows}
            fieldNames={RECIPIENT_FIELDS}
            save={updateInvoiceLimited}
            fields={
                <RecipientsCard
                    invoice={invoice}
                    isInViewMode={isInViewMode}
                    clientParty={invoice.clientParty}
                    updateInvoiceLimited={NO_SAVE}
                    isInCard
                />
            }
        />
    );
}

const INVOICE_DETAILS_FIELDS: Path<InvoiceForm>[] = [
    'invoiceDate',
    'issueDate',
    'dueDate',
    'serviceStartDate',
    'serviceEndDate',
    'serviceDate',
    'invoiceNumber',
    'invoiceDescription',
    'invoiceDescriptionLocalLanguage',
    'outlineDescription',
    'outlineDescriptionLocalLanguage',
    'additionalAddress',
    'additionalInfo',
    'isPaymentCurrencyDifferent',
    'paymentCurrencyType',
    'isDisplayedInPaymentCurrency',
    'baseCurrencyConversionDate',
    'paymentCurrencyConversionDate',
    'invoiceDateManuallyUpdated',
    'issueDateManuallyUpdated',
    'dueDateManuallyUpdated',
];

/**
 * Invoice details. Recalculate dates saves at once, so it is an action of the read view in the card's header, never a
 * button inside the form whose changes wait for Save.
 */
export function InvoiceDetailsSection({
    invoice,
    classifiers,
    isInViewMode,
    submitInvoice,
    existingPrepayments,
}: Pick<InvoiceTabProps, 'invoice' | 'classifiers' | 'isInViewMode' | 'submitInvoice' | 'existingPrepayments'>) {
    useServiceDateAutofill();
    const recalculateDates = useRecalculateInvoiceDates(invoice.id);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const isRecalculationEnabled = useIsFeatureEnabled(FeatureFlag.CUSTOM_SERVICE_PERIOD_RECALC_LOGIC);
    const isIssueDateEnabled = useIsFeatureEnabled(FeatureFlag.ISSUE_DATE_FIELD_ON_INVOICE);
    const isServiceDateControlEnabled = useIsFeatureEnabled(FeatureFlag.SERVICE_DATE_CONTROL);
    const isBaseConversionDateEnabled = useIsFeatureEnabled(FeatureFlag.BASE_CURRENCY_CONVERSION_DATE_CONTROL);
    const isPaymentConversionDateEnabled = useIsFeatureEnabled(FeatureFlag.PAYMENT_CURRENCY_CONVERSION_DATE_CONTROL);
    const isDisplayInPaymentCurrencyEnabled = useIsFeatureEnabled(
        FeatureFlag.INVOICE_FIELDS_VALUES_IN_PAYMENT_CURRENCY
    );
    const { control } = useFormContext<InvoiceForm>();
    const [
        invoiceDate,
        issueDate,
        dueDate,
        serviceStartDate,
        serviceEndDate,
        serviceDate,
        invoiceNumber,
        invoiceDescription,
        invoiceDescriptionLocalLanguage,
        outlineDescription,
        outlineDescriptionLocalLanguage,
        additionalAddress,
        additionalInfo,
        invoiceCurrencyType,
        isPaymentCurrencyDifferent,
        paymentCurrencyType,
        isDisplayedInPaymentCurrency,
        baseCurrencyConversionDate,
        paymentCurrencyConversionDate,
    ] = useWatch({
        control,
        name: [
            'invoiceDate',
            'issueDate',
            'dueDate',
            'serviceStartDate',
            'serviceEndDate',
            'serviceDate',
            'invoiceNumber',
            'invoiceDescription',
            'invoiceDescriptionLocalLanguage',
            'outlineDescription',
            'outlineDescriptionLocalLanguage',
            'additionalAddress',
            'additionalInfo',
            'invoiceCurrencyType',
            'isPaymentCurrencyDifferent',
            'paymentCurrencyType',
            'isDisplayedInPaymentCurrency',
            'baseCurrencyConversionDate',
            'paymentCurrencyConversionDate',
        ],
    });
    const isPaymentCurrencySelected =
        !!paymentCurrencyType && !!invoiceCurrencyType && paymentCurrencyType.value !== invoiceCurrencyType.value;
    // A paid invoice keeps its payment currency, as classic's hint beside the field says.
    const hasInvoicePayments =
        !!invoice.invoicePayments?.length || !!existingPrepayments?.some((prepayment) => prepayment.isConnected);
    const rows: Row[] = [
        { label: t('invoices.general-tab.invoice-details.invoice-date'), content: dateCell(invoiceDate) },
        {
            label: t('invoices.general-tab.invoice-details.issue-date'),
            content: dateCell(issueDate),
            hidden: !isIssueDateEnabled,
        },
        {
            label: t('invoices.general-tab.invoice-details.due-date'),
            content: dateCell(dueDate),
            hidden: invoice.invoiceStatusTypeCode === InvoiceStatusType.CREDITED,
        },
        { label: t('invoices.general-tab.invoice-details.service-start-date'), content: dateCell(serviceStartDate) },
        { label: t('invoices.general-tab.invoice-details.service-end-date'), content: dateCell(serviceEndDate) },
        {
            label: t('invoices.general-tab.invoice-details.service-date'),
            content: dateCell(serviceDate),
            hidden: !isServiceDateControlEnabled,
        },
        { label: t('invoices.general-tab.invoice-details.invoice-number'), content: invoiceNumber || '-' },
        {
            label: smallInfoLabel(
                'invoices.general-tab.invoice-details.reference-number',
                'invoices.general-tab.invoice-details.reference-number-tooltip'
            ),
            content: invoice.referenceNumber || '-',
            hidden: !invoice.referenceNumber,
        },
        {
            label: t('invoices.general-tab.invoice-details.invoice-currency'),
            content: invoiceCurrencyType?.label || '-',
        },
        {
            label: t('invoices.general-tab.invoice-details.invoice-description'),
            content: invoiceDescription || '-',
            multiline: true,
        },
        {
            label: t('invoices.general-tab.invoice-details.invoice-description-local-language'),
            content: invoiceDescriptionLocalLanguage || '-',
            multiline: true,
        },
        {
            label: t('invoices.general-tab.invoice-details.outline-description'),
            content: outlineDescription || '-',
            multiline: true,
        },
        {
            label: t('invoices.general-tab.invoice-details.outline-description-local-language'),
            content: outlineDescriptionLocalLanguage || '-',
            multiline: true,
        },
        {
            label: t('invoices.general-tab.invoice-details.additional-address'),
            content: additionalAddress || '-',
            multiline: true,
        },
        {
            label: t('invoices.general-tab.invoice-details.additional-info'),
            content: additionalInfo || '-',
            multiline: true,
        },
        {
            label: hasInvoicePayments
                ? smallInfoLabel(
                      'invoices.general-tab.invoice-details.use-different-payment-currency',
                      'invoices.tooltips.cannot-change-payment-currency'
                  )
                : t('invoices.general-tab.invoice-details.use-different-payment-currency'),
            content: yesNo(isPaymentCurrencyDifferent),
        },
        {
            label: t('invoices.general-tab.invoice-details.payment-currency'),
            content: paymentCurrencyType?.label || '-',
            hidden: !isPaymentCurrencyDifferent,
        },
        {
            label: t('invoices.general-tab.invoice-details.display-in-payment-currency'),
            content: yesNo(isDisplayedInPaymentCurrency),
            hidden: !(isDisplayInPaymentCurrencyEnabled && isPaymentCurrencyDifferent && isPaymentCurrencySelected),
        },
        {
            label: t('invoices.general-tab.invoice-details.base-currency-conversion-date'),
            content: dateCell(baseCurrencyConversionDate),
            hidden: !isBaseConversionDateEnabled,
        },
        {
            label: t('invoices.general-tab.invoice-details.payment-currency-conversion-date'),
            content: dateCell(paymentCurrencyConversionDate),
            hidden: !isPaymentConversionDateEnabled,
        },
    ];

    const recalculate = async () => {
        setIsRecalculating(true);
        await recalculateDates();
        setIsRecalculating(false);
    };

    return (
        <InvoiceFormSection
            id="invoice-details"
            titleId="invoices.general-tab.invoice-details.title"
            invoice={invoice}
            canEdit={!isInViewMode}
            rows={rows}
            headerAction={
                isRecalculationEnabled ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        className={EDIT_BUTTON_CLASS}
                        disabled={isRecalculating}
                        focusableWhenDisabled
                        onClick={() => void recalculate()}
                    >
                        <RefreshCwIcon
                            data-icon="inline-start"
                            className={cn('size-3.5', isRecalculating && 'motion-safe:animate-spin')}
                        />
                        <FormattedMessage id="invoices.general-tab.invoice-details.recalculate-dates" />
                    </Button>
                ) : undefined
            }
            fieldNames={INVOICE_DETAILS_FIELDS}
            save={submitInvoice}
            fields={
                <InvoiceDetailsCard
                    classifiers={classifiers}
                    isInViewMode={isInViewMode}
                    invoice={invoice}
                    submitInvoice={NO_SAVE}
                    existingPrepayments={existingPrepayments}
                    recalculatesInHeader
                />
            }
        />
    );
}

/** A value with who set it and when under it. */
function SetByValue({ value, setBy }: { value: ReactNode; setBy: string | null }) {
    return (
        <span className="flex flex-col">
            <span>{value || '-'}</span>
            {setBy ? <span className="text-muted-foreground text-caption font-normal">{setBy}</span> : null}
        </span>
    );
}

const DEBT_COLLECTION_FIELDS: Path<InvoiceForm>[] = [
    'debtNotes',
    'nextReminderDate',
    'receivabilityType',
    'penaltyAmount',
    'interestRate',
    'interestPaidOn',
    'nextDebtCollectionActionType',
];

/**
 * Debt collection. It edits whatever the invoice's state, on the right to edit billing notes, as classic's fields
 * do; a postponed invoice locks it.
 */
export function DebtCollectionSection({ invoice }: Pick<InvoiceTabProps, 'invoice'>) {
    const { control } = useFormContext<InvoiceForm>();
    const [debtNotes, nextReminderDate, receivabilityType, penaltyAmount, interestRate, interestPaidOn, nextAction] =
        useWatch({
            control,
            name: [
                'debtNotes',
                'nextReminderDate',
                'receivabilityType',
                'penaltyAmount',
                'interestRate',
                'interestPaidOn',
                'nextDebtCollectionActionType',
            ],
        });
    const saveDebtNotes = useSaveDebtNotes(invoice.id);
    const auditInfo = useDebtCollectionAuditInfo();
    const canUpdate = useHasPermissions(['UPDATE.INVOICES.BILLING_NOTES']);
    const { isPostponed } = useInvoice(invoice);
    const receivabilityLabel = t('invoices.receivability');

    const rows: Row[] = [
        { label: t('invoices.debt-collection-notes'), content: debtNotes || '-', multiline: true },
        { label: t('invoices.next-reminder-date'), content: dateCell(nextReminderDate) },
        {
            label: (
                <span className="flex items-center gap-1">
                    {receivabilityLabel}
                    <InfoTooltip text="invoices.tooltips.receivability" label={receivabilityLabel} small />
                    <DisplayHistoryButton
                        entityType={HistoryEntityType.INVOICE}
                        entityId={`${invoice.id}`}
                        fieldType={HistoryFieldType.RECEIVABILITY_TYPE_CODE}
                        fieldName={receivabilityLabel}
                        activity={t('updating')}
                        detached={receivabilityHistorySheet(invoice)}
                        plain
                    />
                </span>
            ),
            content: (
                <SetByValue
                    value={receivabilityType?.label}
                    setBy={auditInfo(invoice.receivabilitySetAt, invoice.receivabilitySetBy)}
                />
            ),
        },
        {
            label: smallInfoLabel('invoices.penalty-amount', 'invoices.tooltips.penalty-amount'),
            content: penaltyAmount || '-',
        },
        { label: t('invoices.interest-rate'), content: interestRate ? `${interestRate}%` : '-' },
        { label: t('invoices.interest-paid-on'), content: dateCell(interestPaidOn) },
        {
            label: smallInfoLabel(
                'invoices.next-debt-collection-action',
                'invoices.tooltips.next-debt-collection-action'
            ),
            content: (
                <SetByValue
                    value={nextAction?.label}
                    setBy={auditInfo(invoice.nextDebtCollectionActionSetAt, invoice.nextDebtCollectionActionSetBy)}
                />
            ),
        },
    ];

    return (
        // Links to the debt notes land here.
        <div id="debt-notes">
            <InvoiceFormSection
                id="debt-collection"
                titleId="invoices.debt-collection"
                invoice={invoice}
                canEdit={canUpdate && !isPostponed}
                lockMessageId={isPostponed ? undefined : 'invoices.general-tab.lock.read-only'}
                rows={rows}
                fieldNames={DEBT_COLLECTION_FIELDS}
                save={async () => {
                    await saveDebtNotes().unwrap();
                }}
                fields={<DebtCollectionCard invoice={invoice} isInCard />}
            />
        </div>
    );
}
